/**
 * A simple Pub/Sub system for managing custom events.
 *
 * Example:
 * ```js
 * const unsubscribe = subscribe("cart:add", (item) => console.log(item));
 * publish("cart:add", { id: 1, name: "Shirt" });
 * unsubscribe();
 * ```
 */

let subscribers = {};

/**
 * Subscribes to an event and returns an unsubscribe function.
 *
 * @param {string} eventName - The name of the event to listen for.
 * @param {(data: any) => void} callback - The callback to execute when the event is published.
 * @returns {() => void} A function that unsubscribes this callback from the event.
 */
export function subscribe(eventName, callback) {
  if (!subscribers[eventName]) {
    subscribers[eventName] = [];
  }

  if (!subscribers[eventName].includes(callback)) {
    subscribers[eventName].push(callback);
  }

  return function unsubscribe() {
    subscribers[eventName] = subscribers[eventName].filter(cb => cb !== callback);
    if (subscribers[eventName].length === 0) {
      delete subscribers[eventName];
    }
  };
}

/**
 * Publishes an event, invoking all subscribed callbacks.
 *
 * @param {string} eventName - The name of the event to publish.
 * @param {any} [data] - Optional data to pass to the event subscribers.
 * @returns {void}
 */
export function publish(eventName, data) {
  if (subscribers[eventName]) {
    subscribers[eventName].forEach(callback => {
      try {
        callback(data);
      } catch (err) {
        console.error(`Error in subscriber for ${eventName}:`, err);
      }
    });
  }
}